import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IUsuario, User } from '@core/user/user.types';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { environment } from 'environments/environment';
import { Observable, of, switchMap, throwError, Subscription, BehaviorSubject, catchError, timeout } from 'rxjs';
import { NavigationService } from '@core/navigation/navigation.service';

export interface LogoutNotice {
    type: 'success' | 'error' | 'info' | 'warning';
    title: string;
    message: string;
    duration?: number;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
    private readonly ACCESS_TOKEN_KEY = 'accessToken';
    private readonly EMBED_ACCESS_TOKEN_KEY = 'embedAccessToken';
    private _authenticated: boolean = false;
    private user: IUsuario;
    private _navigationMenu: any[] = [];
    private rutas: string[] = [];
    rutasListas$ = new BehaviorSubject<boolean>(false);
    tiposArticuloPermitidos: string[] = [];
    avisado = false;
    private subscription: Subscription;
    private readonly logoutNoticeKey = 'auth.logout.notice';

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService,
        private _navigationService: NavigationService,
    ) { }

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        const normalizedToken = (token || '').trim();

        if (!normalizedToken || !AuthUtils.isValidJwt(normalizedToken) || AuthUtils.isTokenExpired(normalizedToken)) {
            localStorage.removeItem(this.ACCESS_TOKEN_KEY);
            this._authenticated = false;
            return;
        }

        localStorage.setItem(this.ACCESS_TOKEN_KEY, normalizedToken);
    }

    get accessToken(): string {
        return localStorage.getItem(this.ACCESS_TOKEN_KEY) ?? '';
    }

    get embedAccessToken(): string {
        return localStorage.getItem(this.EMBED_ACCESS_TOKEN_KEY) ?? '';
    }

    applyExternalToken(token: string | null | undefined, options?: { embedded?: boolean }): boolean {
        const normalizedToken = (token || '').trim();

        if (!normalizedToken) {
            return false;
        }

        if (!AuthUtils.isValidJwt(normalizedToken) || AuthUtils.isTokenExpired(normalizedToken)) {
            return false;
        }

        if (options?.embedded) {
            localStorage.setItem(this.EMBED_ACCESS_TOKEN_KEY, normalizedToken);
            this._authenticated = true;
            return true;
        }

        this.accessToken = normalizedToken;
        if (!this.accessToken) {
            return false;
        }

        this._authenticated = true;
        this.ensureNavigationMenuLoaded();
        return true;
    }

    clearEmbedToken(): void {
        localStorage.removeItem(this.EMBED_ACCESS_TOKEN_KEY);
    }

    isEmbedContext(): boolean {
        if (typeof window === 'undefined') {
            return false;
        }

        const searchParams = new URLSearchParams(window.location.search || '');
        if (searchParams.get('embed') === '1') {
            return true;
        }

        const path = (window.location.pathname || '').toLowerCase();
        return path.includes('/embed') || path.includes('/jedstion-embed');
    }

    /**
     * Setter & getter for navigation menu
     */
    set navigationMenu(menu) {
        this._navigationMenu = menu;
    }

    get navigationMenu() {
        return this._navigationMenu;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Métodos públicos
    // -----------------------------------------------------------------------------------------------------

    /**
     * Recuperar contraseña
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any> {
        return this._httpClient.post('api/auth/forgot-password', email);
    }

    /**
     * Restablecer contraseña
     *
     * @param password
     */
    resetPassword(password: string): Observable<any> {
        return this._httpClient.post('api/auth/reset-password', password);
    }

    /**
     * Iniciar sesión
     *
     * @param credentials
     */
    signIn(credentials): Observable<any> {

        return this._httpClient.post(`${environment.login.login}`, credentials).pipe(
            switchMap((response: any) => {
                if (response.success) {
                    // Almacenar el token de acceso en el almacenamiento local
                    this.accessToken = response.token;
                    if (!this.accessToken) {
                        this._authenticated = false;
                        return throwError(() => new Error('Token inválido recibido en login'));
                    }
                    // Atencion sí cambia el estado tira 'undefined' y hay que volver a loguearse
                    // Seteamos el menú correspondiente al usuario logueado
                    this.navigationMenu = response.menu;
                    this._navigationService._navigationMenuData = this.navigationMenu;

                    // const usuario: User = {
                    //     id: response.usuario.codigo,
                    //     name: response.usuario.nick,
                    //     avatar: response.usuario.foto_app,
                    //     email: response.usuario.email,
                    //     status: response.usuario.activo
                    // }
                    // Almacenar el usuario en el servicio de usuario
                    // this._userService.user = usuario;

                    // Establecer el indicador de autenticación en verdadero
                    this._authenticated = true;

                    // Devolver un nuevo observable con la respuesta
                    return of(response);
                } else {
                    // Si no hay éxito, lanzar un error
                    return throwError("Inicio de sesión fallido");
                }
            })
        );
    }

    /**
     * Función que verifica las rutas que tiene asignadas el usuario logueado. Es utilizada por los guards.
     * @param path 
     * @returns boolean
     */
    verificarRutasPermitidas(path?): Observable<boolean> {
        const safePath = (path || '').toString();
        this.ensureNavigationMenuLoaded();

        if (this.rutas.length === 0 && this._navigationService._navigationMenuData?.length > 0) {
            this.extractRoutesFromMenu(this._navigationService._navigationMenuData);
        }

        // Comprobar si las rutas ya están cargadas
        if (this.rutas.length === 0) {
            // Las rutas no están cargadas, cárgalas
            return this.loadRoutes().pipe(
                timeout(10000),
                catchError(() => of(void 0)),
                switchMap(() => {
                    return of(this.hasRouteAccess(safePath));
                })
            );
        } else {
            // Las rutas ya están cargadas
            return of(this.hasRouteAccess(safePath));
        }
    }

    /**
     * Cargar las rutas desde el menú de navegación
    */
    loadRoutes(): Observable<void> {
        return new Observable<void>((observer) => {
            // Esperar a que se establezca el menú de navegación
            this.subscription = this._navigationService.navigation$.subscribe((navigation) => {
                if (navigation && navigation.default && navigation.default.length > 0) {
                    this.extractRoutesFromMenu(navigation.default);
                    this.rutasListas$.next(true);
                    observer.next();
                    observer.complete();
                    if (this.subscription) {
                        this.subscription.unsubscribe();
                    }
                }
            });

            this.ensureNavigationMenuLoaded();
        });
    }

    /**
     * Función para extraer rutas del menú de navegación
     * @param menu
     */
    private extractRoutesFromMenu(menu): void {
        this.rutas = [];
        menu.forEach((item) => {
            // Verificar si el ítem padre tiene una ruta y agregarla
            if (item.link) {
                let rutaPadre = item.link.replace('/', '');
                if (!this.rutas.includes(rutaPadre)) {
                    this.rutas.push(rutaPadre);
                }
            }
            this.tieneHijos(item.children);
        });
    }

    /**
     * Función recursiva que recorre los submenús.
     * @param hijos 
     */
    private tieneHijos(hijos): void {
        hijos.forEach(subItem => {
            if (subItem.link) {
                let ruta = subItem.link.replace('/', '');
                if (!this.rutas.includes(ruta)) {
                    this.rutas.push(ruta);
                }
            }
            if (subItem.children && subItem.children.length > 0) {
                this.tieneHijos(subItem.children);
            }
        });
    }

    /**
     * Cerrar sesión
     */
    signOut(notice?: Partial<LogoutNotice>): Observable<any> {
        if (notice?.message) {
            this.setLogoutNotice({
                type: notice.type ?? 'warning',
                title: notice.title ?? 'Sesión finalizada',
                message: notice.message,
                duration: notice.duration ?? 6,
            });
        }
        // Eliminar el token de acceso del almacenamiento local
        localStorage.clear();
        // Establecer el indicador de autenticación en falso
        this._authenticated = false;
        // Devolver el observable
        return of(true);
    }

    renewToken(): Observable<any> {
        return this._httpClient.post<any>(environment.menu, null);
    }

    /**
     * Registrarse
     *
     * @param user
     */
    signUp(user: { name: string; email: string; password: string; company: string }): Observable<any> {
        return this._httpClient.post('api/auth/sign-up', user);
    }

    /**
     * Desbloquear sesión
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Verificar el estado de autenticación
     */
    check(): Observable<boolean> {
        const primaryToken = this.accessToken;
        const isEmbedContext = this.isEmbedContext();
        const embedToken = isEmbedContext ? this.embedAccessToken : '';
        const token = primaryToken || embedToken;
        const tokenSource: 'primary' | 'embed' | 'none' = primaryToken
            ? 'primary'
            : (embedToken ? 'embed' : 'none');

        // Comprobar la disponibilidad del token de acceso
        if (!token) {
            this._authenticated = false;
            return of(false);
        }

        // Comprobar que el token tenga formato JWT válido
        if (!AuthUtils.isValidJwt(token)) {
            if (tokenSource === 'embed') {
                this.clearEmbedToken();
            } else {
                localStorage.removeItem(this.ACCESS_TOKEN_KEY);
            }
            this._authenticated = false;
            if (tokenSource !== 'embed') {
                this.setLogoutNotice({
                    type: 'warning',
                    title: 'Sesión inválida',
                    message: 'Tu sesión no es válida. Iniciá sesión nuevamente.',
                    duration: 6,
                });
            }
            return of(false);
        }

        // Comprobar la fecha de vencimiento del token de acceso
        if (AuthUtils.isTokenExpired(token)) {
            if (tokenSource === 'embed') {
                this.clearEmbedToken();
            } else {
                localStorage.removeItem(this.ACCESS_TOKEN_KEY);
            }
            this._authenticated = false;
            if (tokenSource !== 'embed') {
                this.setLogoutNotice({
                    type: 'warning',
                    title: 'Sesión expirada',
                    message: 'Tu sesión expiró. Iniciá sesión nuevamente.',
                    duration: 6,
                });
            }
            return of(false);
        }

        // Si el token de acceso existe y no ha caducado, iniciar sesión con él
        this._authenticated = true;
        return of(true);
    }

    consumeLogoutNotice(): LogoutNotice | null {
        try {
            const raw = sessionStorage.getItem(this.logoutNoticeKey);
            if (!raw) {
                return null;
            }

            sessionStorage.removeItem(this.logoutNoticeKey);
            return JSON.parse(raw) as LogoutNotice;
        } catch {
            sessionStorage.removeItem(this.logoutNoticeKey);
            return null;
        }
    }

    getDataUser() {
        return this.user;
    }

    private hasRouteAccess(path: string): boolean {
        const normalizedPath = (path || '').toString().replace(/^\//, '');
        if (!normalizedPath) {
            return false;
        }

        const firstSegment = normalizedPath.split('/')[0];

        return this.rutas.some((route) => {
            const normalizedRoute = (route || '').replace(/^\//, '');
            const routeFirstSegment = normalizedRoute.split('/')[0];

            return normalizedRoute === normalizedPath || routeFirstSegment === firstSegment;
        });
    }

    private ensureNavigationMenuLoaded(): void {
        if (!this._navigationService._navigationMenuData?.length) {
            void this._navigationService.getMenus();
        }
    }

    private setLogoutNotice(notice: LogoutNotice): void {
        try {
            sessionStorage.setItem(this.logoutNoticeKey, JSON.stringify(notice));
        } catch {
            // Ignore storage errors.
        }
    }
}
